import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  View,
  Text,
  Animated,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { Snowflake, Baby, Heart, Telescope } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../App";
import { useOnboarding } from "../OnboardingContext";
import { useStytchUser } from "../../utils/useStytch";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { OnboardingStackParamList, PrimaryIntent } from "../types";
import { MovingGradientTitle } from "../components/OnboardingLayout";
import GlassChoiceButton from "../components/GlassChoiceButton";
import { AUTO_ADVANCE_DELAY } from "../theme";
import { routeAfterPrimaryIntent } from "../onboardingFlow";
import { getOnboardingViewport } from "../onboardingViewport";

type Props = NativeStackScreenProps<OnboardingStackParamList, "WelcomeGoals">;

const PRIMARY_OPTIONS: {
  value: PrimaryIntent;
  labelKey: string;
  sublabelKey: string;
  Icon: typeof Snowflake;
}[] = [
  {
    value: "preserve",
    labelKey: "onboarding.welcome_goals.preserve_label",
    sublabelKey: "onboarding.welcome_goals.preserve_sublabel",
    Icon: Snowflake,
  },
  {
    value: "grow",
    labelKey: "onboarding.welcome_goals.grow_label",
    sublabelKey: "onboarding.welcome_goals.grow_sublabel",
    Icon: Baby,
  },
  {
    value: "donate",
    labelKey: "onboarding.welcome_goals.donate_label",
    sublabelKey: "onboarding.welcome_goals.donate_sublabel",
    Icon: Heart,
  },
  {
    value: "learn",
    labelKey: "onboarding.welcome_goals.learn_label",
    sublabelKey: "onboarding.welcome_goals.learn_sublabel",
    Icon: Telescope,
  },
];

export default function WelcomeGoalsScreen({ navigation }: Props) {
  const { width: viewportWidth, height: viewportHeight } =
    useWindowDimensions();
  const { compact, condensed } = getOnboardingViewport(
    viewportWidth,
    viewportHeight,
  );
  const t = useTranslations();
  const capturePress = usePressCapture("WelcomeGoalsScreen");
  const { data, updateField, startPath } = useOnboarding();
  const { user } = useStytchUser();
  const cardOpacity = useRef(new Animated.Value(1)).current;
  const [selectedIntent, setSelectedIntent] = useState<PrimaryIntent | null>(
    data.primaryIntent,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancing = useRef(false);

  // Welcome is the unauthenticated entry point. Authenticated users can still
  // reach this screen through an explicit `/welcome` deep link, so redirect
  // them before rendering onboarding and keep Home as the only destination.
  useLayoutEffect(() => {
    if (!user) return;

    navigation
      .getParent<NativeStackScreenProps<RootStackParamList>["navigation"]>()
      ?.reset({ index: 0, routes: [{ name: "Home" }] });
  }, [navigation, user]);

  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", () => {
      Animated.timing(cardOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    });
    return unsubscribe;
  }, [navigation, cardOpacity]);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  if (user) return null;

  const openLogin = () => navigation.navigate("Login");

  const handlePrimarySelect = (value: PrimaryIntent) => {
    if (advancing.current) return;
    advancing.current = true;
    setSelectedIntent(value);
    // Path fork: an intent change resets prior answers (#1127).
    startPath(value);
    if (value === "learn") {
      updateField("treatmentIntent", "exploring");
    }
    advanceTimer.current = setTimeout(() => {
      advancing.current = false;
      advanceTimer.current = null;
      const nextRoute = routeAfterPrimaryIntent(value);
      if (nextRoute.name === "JourneyStage") {
        navigation.navigate(nextRoute.name, nextRoute.params);
      } else {
        navigation.navigate(nextRoute.name);
      }
    }, AUTO_ADVANCE_DELAY);
  };

  return (
    <Animated.View
      style={[
        styles.content,
        compact
          ? styles.contentCompact
          : condensed
            ? styles.contentCondensed
            : styles.contentDefault,
        { opacity: cardOpacity },
      ]}
    >
      <MovingGradientTitle
        style={[
          styles.title,
          compact
            ? styles.titleCompact
            : condensed
              ? styles.titleCondensed
              : styles.titleDefault,
        ]}
      >
        {t("onboarding.welcome_goals.title")}
      </MovingGradientTitle>
      <Text
        className="font-dm-sans"
        style={[
          styles.subtitle,
          compact
            ? styles.subtitleCompact
            : condensed
              ? styles.subtitleCondensed
              : styles.subtitleDefault,
        ]}
      >
        {t("onboarding.welcome_goals.subtitle")}
      </Text>

      {/* Option cards */}
      <View
        style={
          compact
            ? styles.optionsCompact
            : condensed
              ? styles.optionsCondensed
              : styles.optionsDefault
        }
      >
        {PRIMARY_OPTIONS.map((opt) => (
          <GlassChoiceButton
            key={opt.value}
            Icon={opt.Icon}
            label={t(opt.labelKey)}
            sublabel={t(opt.sublabelKey)}
            onPress={() => handlePrimarySelect(opt.value)}
            compact={condensed}
            selected={selectedIntent === opt.value}
          />
        ))}
      </View>

      {/* Logged-out users need a direct path to sign in without completing
          onboarding. Authenticated users have already been redirected above. */}
      <View
        style={[
          styles.loginContainer,
          compact
            ? styles.loginContainerCompact
            : condensed
              ? styles.loginContainerCondensed
              : styles.loginContainerDefault,
        ]}
      >
        <Text
          className="font-dm-sans"
          style={[styles.loginText, compact && styles.loginTextCompact]}
        >
          {t("onboarding.welcome_goals.already_have_account")}{" "}
          <Text
            onPress={() => {
              capturePress(
                pressLabel(t("onboarding.welcome_goals.log_in")),
                "onboarding.welcome_goals.log_in",
              );
              openLogin();
            }}
            style={styles.loginLink}
          >
            {t("onboarding.welcome_goals.log_in")}
          </Text>
        </Text>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    width: "100%",
    maxWidth: 480,
    alignSelf: "center",
    justifyContent: "flex-start",
  },
  contentCompact: {
    paddingHorizontal: 12,
    paddingTop: 72,
    paddingBottom: 2,
  },
  contentCondensed: {
    paddingHorizontal: 18,
    paddingTop: 82,
    paddingBottom: 8,
  },
  contentDefault: {
    paddingHorizontal: 24,
    paddingTop: 92,
    paddingBottom: 8,
  },
  title: {
    fontFamily: "Tobias_300Light",
    letterSpacing: -0.8,
    color: "#FFFFFF",
    textAlign: "center",
  },
  titleCompact: {
    fontSize: 30,
    lineHeight: 31,
    marginBottom: 6,
  },
  titleCondensed: {
    fontSize: 32,
    lineHeight: 34,
    marginBottom: 10,
  },
  titleDefault: {
    fontSize: 34,
    lineHeight: 37,
    marginBottom: 10,
  },
  subtitle: {
    color: "rgba(255, 255, 255, 0.72)",
    textAlign: "center",
  },
  subtitleCompact: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  subtitleCondensed: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  subtitleDefault: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 22,
  },
  optionsCompact: {
    gap: 7,
  },
  optionsCondensed: {
    gap: 8,
  },
  optionsDefault: {
    gap: 10,
  },
  loginContainer: {
    alignItems: "center",
  },
  loginContainerCompact: {
    marginTop: 12,
    marginBottom: 2,
  },
  loginContainerCondensed: {
    marginTop: 22,
    marginBottom: 10,
  },
  loginContainerDefault: {
    marginTop: 32,
    marginBottom: 10,
  },
  loginText: {
    fontSize: 13,
    color: "rgba(255, 255, 255, 0.66)",
  },
  loginTextCompact: {
    fontSize: 12,
  },
  loginLink: {
    color: "#FFFFFF",
    fontWeight: "600",
    textDecorationLine: "underline",
  },
});
